import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { TenantService, type Tenant } from '@/services/tenantService';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Plus, Edit, Trash2, ExternalLink, Building2, BarChart3, Copy } from 'lucide-react';
import { LogoUpload } from './LogoUpload';

export const TenantManagement: React.FC = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [copyingProducts, setCopyingProducts] = useState<string | null>(null);

  const [healthChecking, setHealthChecking] = useState(false);

  // Create tenant form state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Edit tenant form state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [updating, setUpdating] = useState(false);
  const [newTenant, setNewTenant] = useState({
    name: '',
    subdomain: '',
    logoUrl: '',
    colorScheme: {
      primary: '#3B82F6',
      secondary: '#1E40AF',
      accent: '#F59E0B',
      background: '#FFFFFF',
      text: '#1F2937',
      // New quick-theming fields
      navbarBg: '#FFFFFF',
      navbarText: '#1F2937',
      navbarHoverText: '#111827',
      startMatrixButtonColor: '#1E40AF',
      getQuotesButtonColor: '#1E40AF',
    },
    bannerConfig: {
      title: 'Comprehensive Business Insurance',
      subtitle: 'Protect your business with our tailored insurance coverage designed for UAE enterprises, provided by our broker partner YallaCompare',
      backgroundColor: '#1E40AF',
      textColor: '#FFFFFF',
      ctaText: 'Get Your Quote',
      ctaLink: '/decision-tree',
      // Optional hero image
      backgroundImage: '',
      // Bottom-of-homepage banner colors
      bottomBannerBackgroundColor: '#1E40AF',
      bottomBannerTextColor: '#FFFFFF',
    }
  });

  const fetchTenants = useCallback(async () => {
    try {
      setLoading(true);
      const tenantsData = await TenantService.getAllTenants();
      setTenants(tenantsData);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load tenants',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  const handleCreateTenant = async () => {
    // Validate required fields with null checks
    if (!newTenant?.name?.trim() || !newTenant?.subdomain?.trim() || !newTenant?.logoUrl?.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Name, subdomain, and logo URL are required',
        variant: 'destructive',
      });
      return;
    }

    // Validate subdomain format
    const subdomainRegex = /^[a-z0-9-]+$/;
    const cleanSubdomain = newTenant.subdomain.trim().toLowerCase();
    if (!subdomainRegex.test(cleanSubdomain)) {
      toast({
        title: 'Validation Error',
        description: 'Subdomain can only contain lowercase letters, numbers, and hyphens',
        variant: 'destructive',
      });
      return;
    }

    // Check for reserved subdomains
    const reservedSubdomains = ['www', 'admin', 'api', 'system', 'app', 'mail', 'ftp'];
    if (reservedSubdomains.includes(cleanSubdomain)) {
      toast({
        title: 'Validation Error',
        description: 'This subdomain is reserved. Please choose a different one.',
        variant: 'destructive',
      });
      return;
    }

    setCreating(true);
    try {
      const tenantData = {
        name: newTenant.name.trim(),
        subdomain: cleanSubdomain,
        logo_url: newTenant.logoUrl.trim(),
        // store all quick-theming values in JSON columns
        color_scheme_json: newTenant.colorScheme,
        banner_config_json: newTenant.bannerConfig,
      };

      console.log('Creating tenant with data:', tenantData);
      const tenant = await TenantService.createTenant(tenantData);
      console.log('Tenant created successfully:', tenant);

      if (tenant && tenant.name) {
        setTenants([tenant, ...tenants]);
        setCreateDialogOpen(false);
        setNewTenant({
          name: '',
          subdomain: '',
          logoUrl: '',
          colorScheme: {
            primary: '#3B82F6',
            secondary: '#1E40AF',
            accent: '#F59E0B',
            background: '#FFFFFF',
            text: '#1F2937'
          },
          bannerConfig: {
            title: 'Comprehensive Business Insurance',
            subtitle: 'Protect your business with our tailored insurance coverage designed for UAE enterprises, provided by our broker partner YallaCompare',
            backgroundColor: '#1E40AF',
            textColor: '#FFFFFF',
            ctaText: 'Get Your Quote',
            ctaLink: '/decision-tree'
          }
        });

        toast({
          title: 'Success',
          description: `Tenant "${tenant.name}" created successfully`,
        });
      } else {
        throw new Error('Invalid tenant data returned from server');
      }
    } catch (error) {
      console.error('Error creating tenant:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create tenant',
        variant: 'destructive',
      });
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteTenant = async (tenantId: string, tenantName: string) => {
    setDeleting(tenantId);
    try {
      console.log('Deleting tenant:', tenantId, tenantName);
      await TenantService.deleteTenant(tenantId);
      setTenants(tenants.filter(t => t.id !== tenantId));

      toast({
        title: 'Success',
        description: `Tenant "${tenantName}" deleted successfully`,
      });
    } catch (error) {
      console.error('Delete tenant error:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete tenant',
        variant: 'destructive',
      });
    } finally {
      setDeleting(null);
    }
  };

  const handleEditTenant = (tenant: Tenant) => {
    // Ensure color_scheme_json is properly initialized
    const tenantWithDefaults = {
      ...tenant,
      color_scheme_json: tenant.color_scheme_json || {
        primary: '#3B82F6',
        secondary: '#6c757d',
        accent: '#28a745',
        background: '#ffffff',
        text: '#212529'
      }
    };
    setEditingTenant(tenantWithDefaults);
    setEditDialogOpen(true);
  };

  const handleUpdateTenant = async () => {
    if (!editingTenant) return;

    setUpdating(true);
    try {
      console.log('Updating tenant with data:', {
        name: editingTenant.name,
        logo_url: editingTenant.logo_url,
        color_scheme_json: editingTenant.color_scheme_json,
        banner_config_json: editingTenant.banner_config_json,
      });

      const updatedTenant = await TenantService.updateTenant(editingTenant.id, {
        name: editingTenant.name,
        logo_url: editingTenant.logo_url,
        color_scheme_json: editingTenant.color_scheme_json,
        banner_config_json: editingTenant.banner_config_json,
        updated_at: new Date().toISOString(),
      });

      setTenants(tenants.map(t => t.id === updatedTenant.id ? updatedTenant : t));
      setEditDialogOpen(false);
      setEditingTenant(null);

      toast({
        title: 'Success',
        description: 'Tenant updated successfully',
      });
    } catch (error) {
      console.error('Update tenant error:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update tenant',
        variant: 'destructive',
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleCopyProducts = async (tenantId: string, tenantName: string) => {
    setCopyingProducts(tenantId);
    try {
      await TenantService.copyProductsForTenant(tenantId);
      toast({
        title: 'Success',
        description: `Products copied successfully for ${tenantName}`,
      });
    } catch (error) {
      console.error('Copy products error:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to copy products',
        variant: 'destructive',
      });
    } finally {
      setCopyingProducts(null);
    }
  };

  const getTenantUrl = (subdomain: string) => {
    if (typeof window === 'undefined') return '#';

    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    const port = window.location.port;

    // Local development: prefer subdomain.localhost with /business base
    if (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname.endsWith('.localhost')
    ) {
      return `${protocol}//${subdomain}.localhost${port ? ':' + port : ''}/business`;
    }

    // Production or non-localhost: use real subdomains
    const parts = hostname.split('.');
    if (parts.length >= 2) {
      const domain = parts.slice(-2).join('.');
      return `${protocol}//${subdomain}.${domain}`;
    }

    return `${protocol}//${subdomain}.${hostname}`;
  };

  // Expose functions to window for debugging
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).updateTenantColors = async (subdomain: string, primaryColor: string) => {
        try {
          const tenant = tenants.find(t => t.subdomain === subdomain);
          if (!tenant) {
            console.error(`Tenant with subdomain '${subdomain}' not found`);
            return;
          }

          const updatedTenant = await TenantService.updateTenant(tenant.id, {
            color_scheme_json: {
              ...tenant.color_scheme_json,
              primary: primaryColor
            },
            updated_at: new Date().toISOString(),
          });

          setTenants(tenants.map(t => t.id === updatedTenant.id ? updatedTenant : t));
          console.log(`✅ Updated ${subdomain} primary color to ${primaryColor}`);
          return updatedTenant;
        } catch (error) {
          console.error('❌ Error updating tenant colors:', error);
          throw error;
        }
      };
    }
  }, [tenants]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Tenant Management</h1>
          <p className="text-muted-foreground">
            Manage all tenant instances and their configurations
          </p>
        </div>

        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Create Tenant
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-2xl w-[95vw] max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Tenant</DialogTitle>
              <DialogDescription>
                Create a new tenant instance with its own subdomain and configuration.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
              <div className="space-y-2">
                <Label htmlFor="tenant-name">Tenant Name *</Label>
                <Input
                  id="tenant-name"
                  placeholder="e.g., Acme Insurance"
                  value={newTenant.name}
                  onChange={(e) => setNewTenant({ ...newTenant, name: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tenant-subdomain">Subdomain *</Label>
                <Input
                  id="tenant-subdomain"
                  placeholder="e.g., acme"
                  value={newTenant.subdomain}
                  onChange={(e) => setNewTenant({ ...newTenant, subdomain: e.target.value.toLowerCase() })}
                />
                <p className="text-xs text-muted-foreground">
                  Only lowercase letters, numbers, and hyphens allowed
                </p>
              </div>

              <LogoUpload
                value={newTenant.logoUrl}
                onChange={(url) => setNewTenant({ ...newTenant, logoUrl: url })}
                tenantName={newTenant.name}
              />

              {/* Color Scheme Section */}
              <div className="space-y-4 border-t pt-4">
                <h4 className="text-sm font-medium">Color Scheme *</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="primary-color">Primary Color</Label>
                    <div className="flex gap-2">
                      <Input
                        id="primary-color"
                        type="color"
                        value={newTenant.colorScheme.primary}
                        onChange={(e) => setNewTenant({
                          ...newTenant,
                          colorScheme: { ...newTenant.colorScheme, primary: e.target.value }
                        })}
                        className="w-16 h-10 p-1 border rounded"
                      />
                      <Input
                        value={newTenant.colorScheme.primary}
                        onChange={(e) => setNewTenant({
                          ...newTenant,
                          colorScheme: { ...newTenant.colorScheme, primary: e.target.value }
                        })}
                        placeholder="#3B82F6"
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="secondary-color">Secondary Color</Label>
                    <div className="flex gap-2">
                      <Input
                        id="secondary-color"
                        type="color"
                        value={newTenant.colorScheme.secondary}
                        onChange={(e) => setNewTenant({
                          ...newTenant,
                          colorScheme: { ...newTenant.colorScheme, secondary: e.target.value }
                        })}
                        className="w-16 h-10 p-1 border rounded"
                      />
                      <Input
                        value={newTenant.colorScheme.secondary}
                        onChange={(e) => setNewTenant({
                          ...newTenant,
                          colorScheme: { ...newTenant.colorScheme, secondary: e.target.value }
                        })}
                        placeholder="#1E40AF"
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="accent-color">Accent Color</Label>
                    <div className="flex gap-2">
                      <Input
                        id="accent-color"
                        type="color"
                        value={newTenant.colorScheme.accent}
                        onChange={(e) => setNewTenant({
                          ...newTenant,
                          colorScheme: { ...newTenant.colorScheme, accent: e.target.value }
                        })}
                        className="w-16 h-10 p-1 border rounded"
                      />
                      <Input
                        value={newTenant.colorScheme.accent}
                        onChange={(e) => setNewTenant({
                          ...newTenant,
                          colorScheme: { ...newTenant.colorScheme, accent: e.target.value }
                        })}
                        placeholder="#F59E0B"
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="text-color">Text Color</Label>
                    <div className="flex gap-2">
                      <Input
                        id="text-color"
                        type="color"
                        value={newTenant.colorScheme.text}
                        onChange={(e) => setNewTenant({
                          ...newTenant,
                          colorScheme: { ...newTenant.colorScheme, text: e.target.value }
                        })}
                        className="w-16 h-10 p-1 border rounded"
                      />
                      <Input
                        value={newTenant.colorScheme.text}
                        onChange={(e) => setNewTenant({
                          ...newTenant,
                          colorScheme: { ...newTenant.colorScheme, text: e.target.value }
                        })}
                        placeholder="#1F2937"
                        className="flex-1"
                      />
                    </div>
                  </div>

                  {/* New: Navbar Colors */}
                  <div className="space-y-2">
                    <Label htmlFor="navbar-bg-color">Navbar Background</Label>
                    <div className="flex gap-2">
                      <Input id="navbar-bg-color" type="color" value={newTenant.colorScheme.navbarBg}
                        onChange={(e)=>setNewTenant({...newTenant,colorScheme:{...newTenant.colorScheme,navbarBg:e.target.value}})} className="w-16 h-10 p-1 border rounded" />
                      <Input value={newTenant.colorScheme.navbarBg}
                        onChange={(e)=>setNewTenant({...newTenant,colorScheme:{...newTenant.colorScheme,navbarBg:e.target.value}})} placeholder="#FFFFFF" className="flex-1" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="navbar-text-color">Navbar Text</Label>
                    <div className="flex gap-2">
                      <Input id="navbar-text-color" type="color" value={newTenant.colorScheme.navbarText}
                        onChange={(e)=>setNewTenant({...newTenant,colorScheme:{...newTenant.colorScheme,navbarText:e.target.value}})} className="w-16 h-10 p-1 border rounded" />
                      <Input value={newTenant.colorScheme.navbarText}
                        onChange={(e)=>setNewTenant({...newTenant,colorScheme:{...newTenant.colorScheme,navbarText:e.target.value}})} placeholder="#1F2937" className="flex-1" />
                    </div>
                  </div>

                  {/* New: Button Colors */}
                  <div className="space-y-2">
                    <Label htmlFor="start-matrix-color">Start Matrix Button</Label>
                    <div className="flex gap-2">
                      <Input id="start-matrix-color" type="color" value={newTenant.colorScheme.startMatrixButtonColor}
                        onChange={(e)=>setNewTenant({...newTenant,colorScheme:{...newTenant.colorScheme,startMatrixButtonColor:e.target.value}})} className="w-16 h-10 p-1 border rounded" />
                      <Input value={newTenant.colorScheme.startMatrixButtonColor}
                        onChange={(e)=>setNewTenant({...newTenant,colorScheme:{...newTenant.colorScheme,startMatrixButtonColor:e.target.value}})} placeholder="#1E40AF" className="flex-1" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="get-quotes-color">Get Quotes CTA Button</Label>
                    <div className="flex gap-2">
                      <Input id="get-quotes-color" type="color" value={newTenant.colorScheme.getQuotesButtonColor}
                        onChange={(e)=>setNewTenant({...newTenant,colorScheme:{...newTenant.colorScheme,getQuotesButtonColor:e.target.value}})} className="w-16 h-10 p-1 border rounded" />
                      <Input value={newTenant.colorScheme.getQuotesButtonColor}
                        onChange={(e)=>setNewTenant({...newTenant,colorScheme:{...newTenant.colorScheme,getQuotesButtonColor:e.target.value}})} placeholder="#1E40AF" className="flex-1" />
                    </div>
                  </div>
                </div>

                {/* Color Preview */}
                <div className="p-4 border rounded-lg" style={{
                  backgroundColor: newTenant.colorScheme.background,
                  color: newTenant.colorScheme.text
                }}>
                  <div className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded" style={{ backgroundColor: newTenant.colorScheme.primary }}></div>
                    <div className="w-8 h-8 rounded" style={{ backgroundColor: newTenant.colorScheme.secondary }}></div>
                    <div className="w-8 h-8 rounded" style={{ backgroundColor: newTenant.colorScheme.accent }}></div>
                    <div className="w-8 h-8 rounded border" title="Navbar BG" style={{ backgroundColor: newTenant.colorScheme.navbarBg }}></div>
                    <span className="text-sm">Preview</span>
                  </div>
                </div>
              </div>

              {/* Banner Configuration Section */}
              <div className="space-y-4 border-t pt-4">
                <h4 className="text-sm font-medium">Homepage Banner Configuration</h4>

                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="banner-title">Banner Title</Label>
                    <Input
                      id="banner-title"
                      placeholder="Comprehensive Business Insurance Solutions"
                      value={newTenant.bannerConfig.title}
                      onChange={(e) => setNewTenant({
                        ...newTenant,
                        bannerConfig: { ...newTenant.bannerConfig, title: e.target.value }
                      })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="banner-subtitle">Banner Subtitle</Label>
                    <Input
                      id="banner-subtitle"
                      placeholder="Protect your business with our tailored insurance coverage"
                      value={newTenant.bannerConfig.subtitle}
                      onChange={(e) => setNewTenant({
                        ...newTenant,
                        bannerConfig: { ...newTenant.bannerConfig, subtitle: e.target.value }
                      })}
                    />
                  </div>

                  {/* New: Banner Background Image */}
                  <div className="space-y-2">
                    <Label htmlFor="banner-bg-image">Banner Background Image URL (optional)</Label>
                    <Input
                      id="banner-bg-image"
                      placeholder="https://example.com/hero.jpg"
                      value={newTenant.bannerConfig.backgroundImage || ''}
                      onChange={(e) => setNewTenant({
                        ...newTenant,
                        bannerConfig: { ...newTenant.bannerConfig, backgroundImage: e.target.value }
                      })}
                    />
                    <p className="text-xs text-muted-foreground">If set, we will cover/center the image with a color overlay (below).</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="banner-bg-color">Banner Background Color / Overlay</Label>
                      <div className="flex gap-2">
                        <Input
                          id="banner-bg-color"
                          type="color"
                          value={newTenant.bannerConfig.backgroundColor}
                          onChange={(e) => setNewTenant({
                            ...newTenant,
                            bannerConfig: { ...newTenant.bannerConfig, backgroundColor: e.target.value }
                          })}
                          className="w-16 h-10 p-1 border rounded"
                        />
                        <Input
                          value={newTenant.bannerConfig.backgroundColor}
                          onChange={(e) => setNewTenant({
                            ...newTenant,
                            bannerConfig: { ...newTenant.bannerConfig, backgroundColor: e.target.value }
                          })}
                          placeholder="#1E40AF"
                          className="flex-1"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="banner-text-color">Banner Text Color</Label>
                      <div className="flex gap-2">
                        <Input
                          id="banner-text-color"
                          type="color"
                          value={newTenant.bannerConfig.textColor}
                          onChange={(e) => setNewTenant({
                            ...newTenant,
                            bannerConfig: { ...newTenant.bannerConfig, textColor: e.target.value }
                          })}
                          className="w-16 h-10 p-1 border rounded"
                        />
                        <Input
                          value={newTenant.bannerConfig.textColor}
                          onChange={(e) => setNewTenant({
                            ...newTenant,
                            bannerConfig: { ...newTenant.bannerConfig, textColor: e.target.value }
                          })}
                          placeholder="#FFFFFF"
                          className="flex-1"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="banner-cta-text">Call-to-Action Text</Label>
                      <Input
                        id="banner-cta-text"
                        placeholder="Get Your Quote"
                        value={newTenant.bannerConfig.ctaText}
                        onChange={(e) => setNewTenant({
                          ...newTenant,
                          bannerConfig: { ...newTenant.bannerConfig, ctaText: e.target.value }
                        })}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="banner-cta-link">Call-to-Action Link</Label>
                      <Input
                        id="banner-cta-link"
                        placeholder="/decision-tree"
                        value={newTenant.bannerConfig.ctaLink}
                        onChange={(e) => setNewTenant({
                          ...newTenant,
                          bannerConfig: { ...newTenant.bannerConfig, ctaLink: e.target.value }
                        })}
                      />
                    </div>
                  </div>

                  {/* New: Bottom-of-homepage banner colors */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="bottom-banner-bg">Bottom Banner Background</Label>
                      <div className="flex gap-2">
                        <Input id="bottom-banner-bg" type="color" value={newTenant.bannerConfig.bottomBannerBackgroundColor}
                          onChange={(e)=>setNewTenant({...newTenant,bannerConfig:{...newTenant.bannerConfig,bottomBannerBackgroundColor:e.target.value}})} className="w-16 h-10 p-1 border rounded" />
                        <Input value={newTenant.bannerConfig.bottomBannerBackgroundColor}
                          onChange={(e)=>setNewTenant({...newTenant,bannerConfig:{...newTenant.bannerConfig,bottomBannerBackgroundColor:e.target.value}})} placeholder="#1E40AF" className="flex-1" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="bottom-banner-text">Bottom Banner Text</Label>
                      <div className="flex gap-2">
                        <Input id="bottom-banner-text" type="color" value={newTenant.bannerConfig.bottomBannerTextColor}
                          onChange={(e)=>setNewTenant({...newTenant,bannerConfig:{...newTenant.bannerConfig,bottomBannerTextColor:e.target.value}})} className="w-16 h-10 p-1 border rounded" />
                        <Input value={newTenant.bannerConfig.bottomBannerTextColor}
                          onChange={(e)=>setNewTenant({...newTenant,bannerConfig:{...newTenant.bannerConfig,bottomBannerTextColor:e.target.value}})} placeholder="#FFFFFF" className="flex-1" />
                      </div>
                    </div>
                  </div>

                  {/* Banner Preview */}
                  <div
                    className="p-6 border rounded-lg text-center"
                    style={{
                      backgroundColor: newTenant.bannerConfig.backgroundColor,
                      color: newTenant.bannerConfig.textColor,
                      backgroundImage: newTenant.bannerConfig.backgroundImage ? `url(${newTenant.bannerConfig.backgroundImage})` : undefined,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  >
                    <div className="space-y-2 mb-3">
                      <h3 className="text-lg font-bold">{newTenant.bannerConfig.title || 'Banner Title'}</h3>

                      {/* Prominent YallaCompare Branding */}
                      <div className="flex items-center justify-center gap-2 opacity-90">
                        <div className="h-px bg-current flex-1 max-w-8"></div>
                        <p className="text-xs font-medium tracking-wide">
                          Powered by YallaCompare Insurance Broker LLC #20
                        </p>
                        <div className="h-px bg-current flex-1 max-w-8"></div>
                      </div>
                    </div>

                    <p className="text-sm opacity-90 mb-3">{newTenant.bannerConfig.subtitle || 'Banner subtitle'}</p>
                    <div
                      className="inline-block px-4 py-2 rounded text-sm font-medium mb-4"
                      style={{
                        backgroundColor: newTenant.bannerConfig.textColor === '#FFFFFF' ? '#1F2937' : '#FFFFFF',
                        color: newTenant.bannerConfig.textColor === '#FFFFFF' ? '#FFFFFF' : '#1F2937',
                      }}
                    >
                      {newTenant.bannerConfig.ctaText || 'Call to Action'}
                    </div>

                    {/* Bottom banner preview */}
                    <div className="mt-4 p-4 rounded" style={{backgroundColor:newTenant.bannerConfig.bottomBannerBackgroundColor,color:newTenant.bannerConfig.bottomBannerTextColor}}>
                      <div className="text-sm">Bottom banner preview (contact CTA area)</div>
                    </div>

                    {/* Enhanced Legal Text */}
                    <div className="pt-3 border-t border-white/20">
                      <p className="text-sm opacity-85 font-medium">
                        Powered by YallaCompare Insurance Broker LLC #20
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="sticky bottom-0 bg-background pt-4 -mx-6 px-6 border-t">
              <Button
                variant="outline"
                onClick={() => setCreateDialogOpen(false)}
                disabled={creating}
              >
                Cancel
              </Button>
              <Button onClick={handleCreateTenant} disabled={creating}>
                {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Create Tenant
              </Button>
            </DialogFooter>

        {/* Health Check */}
        <div className="flex items-center justify-end">
          <Button
            variant="outline"
            onClick={async () => {
              setHealthChecking(true);
              try {
                const result = await TenantService.runHealthCheck();
                const detail = `select=${result.details.select}, insert=${result.details.insert}, delete=${result.details.del}`;
                if (result.ok) {
                  toast({ title: 'Health Check Passed', description: detail });
                } else {
                  toast({ title: 'Health Check Issues', description: result.messages.join(' | '), variant: 'destructive' });
                }
              } catch (e) {
                toast({ title: 'Health Check Error', description: e instanceof Error ? e.message : 'Unknown error', variant: 'destructive' });
              } finally {
                setHealthChecking(false);
              }
            }}
            disabled={healthChecking}
          >
            {healthChecking ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Run Health Check
          </Button>
        </div>

          </DialogContent>
        </Dialog>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Tenants</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{tenants.length}</div>
            <p className="text-xs text-muted-foreground">
              Active tenant instances
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tenants Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Tenants</CardTitle>
          <CardDescription>
            Manage and monitor all tenant instances
          </CardDescription>
        </CardHeader>
        <CardContent>
          {tenants.length === 0 ? (
            <div className="text-center py-8">
              <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Tenants Found</h3>
              <p className="text-muted-foreground mb-4">
                Create your first tenant to get started with multi-tenancy.
              </p>
              <Button onClick={() => setCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create First Tenant
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Subdomain</TableHead>
                  <TableHead>Logo</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenants.map((tenant) => (
                  <TableRow key={tenant.id}>
                    <TableCell className="font-medium">{tenant.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{tenant.subdomain}</Badge>
                    </TableCell>
                    <TableCell>
                      {tenant.logo_url ? (
                        <img
                          src={tenant.logo_url}
                          alt={`${tenant.name} logo`}
                          className="h-8 w-8 object-contain rounded"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : (
                        <span className="text-muted-foreground text-sm">No logo</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {tenant.created_at ? new Date(tenant.created_at).toLocaleDateString() : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/system/tenant/${tenant.subdomain}`)}
                          title="View Dashboard"
                        >
                          <BarChart3 className="h-4 w-4" />
                        </Button>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(getTenantUrl(tenant.subdomain), '_blank')}
                          title="Open Site"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditTenant(tenant)}
                          title="Edit Tenant"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCopyProducts(tenant.id, tenant.name)}
                          disabled={copyingProducts === tenant.id}
                          title="Copy Products from Main Site"
                        >
                          {copyingProducts === tenant.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>

                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={deleting === tenant.id}
                            >
                              {deleting === tenant.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Tenant</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete "{tenant.name}"? This action cannot be undone and will permanently delete all associated data including leads, submissions, and configurations.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteTenant(tenant.id, tenant.name)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Delete Tenant
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Tenant Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Tenant</DialogTitle>
            <DialogDescription>
              Update tenant information
            </DialogDescription>
          </DialogHeader>

          {editingTenant && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Tenant Name</Label>
                <Input
                  id="edit-name"
                  value={editingTenant.name}
                  onChange={(e) => setEditingTenant({
                    ...editingTenant,
                    name: e.target.value
                  })}
                  placeholder="Enter tenant name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-subdomain">Subdomain (Read-only)</Label>
                <Input
                  id="edit-subdomain"
                  value={editingTenant.subdomain}
                  disabled
                  className="bg-gray-50"
                />
                <p className="text-xs text-muted-foreground">
                  Subdomain cannot be changed after creation
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-logo">Logo URL</Label>
                <Input
                  id="edit-logo"
                  value={editingTenant.logo_url || ''}
                  onChange={(e) => setEditingTenant({
                    ...editingTenant,
                    logo_url: e.target.value
                  })}
                  placeholder="https://example.com/logo.png"
                />
              </div>

              {/* Color Scheme Section */}
              <div className="space-y-4 border-t pt-4">
                <h4 className="text-sm font-medium">Color Scheme</h4>
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="edit-primary-color">Primary Color (Buttons, CTAs)</Label>
                    <div className="flex gap-2">
                      <Input
                        id="edit-primary-color"
                        type="color"
                        value={editingTenant.color_scheme_json?.primary || '#3B82F6'}
                        onChange={(e) => setEditingTenant({
                          ...editingTenant,
                          color_scheme_json: {
                            ...editingTenant.color_scheme_json,
                            primary: e.target.value
                          }
                        })}
                        className="w-16 h-10 p-1 border rounded"
                      />
                      <Input
                        value={editingTenant.color_scheme_json?.primary || '#3B82F6'}
                        onChange={(e) => setEditingTenant({
                          ...editingTenant,
                          color_scheme_json: {
                            ...editingTenant.color_scheme_json,
                            primary: e.target.value
                          }
                        })}
                        placeholder="#894ee5"
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="edit-secondary-color">Secondary Color</Label>
                    <div className="flex gap-2">
                      <Input
                        id="edit-secondary-color"
                        type="color"
                        value={editingTenant.color_scheme_json?.secondary || '#6c757d'}
                        onChange={(e) => setEditingTenant({
                          ...editingTenant,
                          color_scheme_json: {
                            ...editingTenant.color_scheme_json,
                            secondary: e.target.value
                          }
                        })}
                        className="w-16 h-10 p-1 border rounded"
                      />
                      <Input
                        value={editingTenant.color_scheme_json?.secondary || '#6c757d'}
                        onChange={(e) => setEditingTenant({
                          ...editingTenant,
                          color_scheme_json: {
                            ...editingTenant.color_scheme_json,
                            secondary: e.target.value
                          }
                        })}
                        placeholder="#6c757d"
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setEditDialogOpen(false);
                setEditingTenant(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleUpdateTenant} disabled={updating}>
              {updating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Update Tenant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};